import { Link, useNavigate } from 'react-router-dom';
import { Button, Text } from '@mantine/core';
import { IconCar, IconHandStop, IconLogin } from '@tabler/icons-react';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useRidesWizard } from '@/store/ridesWizard';
import { ActionCard, ActsLogo, SectionCard } from '@/components/ui';
import { useCopy } from '@/lib/settings';

/**
 * Public landing: the Acts Church wordmark, what Carpool is, two clear choices (I need a ride /
 * I can drive), how it works in three steps and one way to sign in. All wording from settings.
 */
export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useEnrichedAuth();
  const setRole = useRidesWizard((s) => s.setRole);
  const { t, settings } = useCopy();

  const go = (role: 'rider' | 'driver') => {
    setRole(role);
    navigate(role === 'rider' ? '/ride/event' : '/drive/profile');
  };

  const riderOpen = settings.riderRegistrationOpen;
  const driverOpen = settings.driverRegistrationOpen;
  const steps = [t('landing.step1'), t('landing.step2'), t('landing.step3')];

  return (
    <div className="rides-main">
      <header className="rides-hero">
        <ActsLogo height={44} />
        <h1 className="rides-title">{t('landing.headline')}</h1>
        <p className="rides-lede">{t('landing.intro')}</p>
      </header>

      <div className="rides-stack" role="group" aria-label="How would you like to start?">
        <ActionCard
          primary
          icon={<IconHandStop size={28} />}
          title={t('landing.rider_card_title')}
          description={riderOpen ? t('landing.rider_card_text') : t('landing.closed')}
          onClick={() => go('rider')}
          disabled={!riderOpen}
        />
        <ActionCard
          icon={<IconCar size={28} />}
          title={t('landing.driver_card_title')}
          description={driverOpen ? t('landing.driver_card_text') : t('landing.closed')}
          onClick={() => go('driver')}
          disabled={!driverOpen}
        />
      </div>

      <div style={{ marginTop: 20 }}>
        {isAuthenticated ? (
          <Button component={Link} to="/home" variant="default" fullWidth>Go to my rides</Button>
        ) : (
          <>
            <Text ta="center" c="dimmed" mb={8}>{t('landing.signin_prompt')}</Text>
            <Button component={Link} to="/auth/login?redirect=%2Fhome" variant="default" fullWidth leftSection={<IconLogin size={20} />}>
              Sign in
            </Button>
          </>
        )}
      </div>

      <div style={{ marginTop: 28 }}>
        <SectionCard title="How it works">
          <ol className="rides-steplist">
            {steps.map((s, i) => (
              <li key={i}>
                <span className="n" aria-hidden="true">{i + 1}</span>
                <span className="s">{s}</span>
              </li>
            ))}
          </ol>
        </SectionCard>
      </div>
    </div>
  );
}
